import { getV2BaseUrl } from '@/utils/v2-base-url'

/**
 * Module route mappings for v2 redirects.
 * Maps v1 routes to their corresponding module key and v2 route.
 *
 * Extracted from route.js so the matching rules can be unit tested without
 * pulling in Vue, the store and every lazily imported screen.
 */
export const V2_ROUTE_MAPPINGS = {
  '/dashboard/system': {
    moduleKey: 'dashboard',
    v2Route: '/app/dashboard/system',
  },
  // Dynamic route - matches /dashboard/reservation/:id
  '/dashboard/reservation/': {
    moduleKey: 'reservation_page',
    v2Route: '/app/reservation/',
    isDynamic: true,
  },
  '/dashboard/listings': {
    moduleKey: 'owners_listings',
    v2Route: '/app/listings',
  },
  '/dashboard/multi-calendar': {
    moduleKey: 'multi_calendar',
    v2Route: '/app/calendar',
  },
  '/dashboard/guest-experience': { moduleKey: 'crm', v2Route: '/app/crm' },
  '/dashboard/service-calls': {
    moduleKey: 'operations_field_ops',
    v2Route: '/app/dashboard/service-calls',
  },
  '/dashboard/listing-task/': {
    moduleKey: 'operations_field_ops',
    v2Route: '/app/dashboard/listing-task/',
    isDynamic: true,
  },
  '/dashboard/taskim': {
    moduleKey: 'operations_office_tasks',
    v2Route: '/app/taskim/query',
  },
  '/dashboard/iot': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot-devices',
  },
  // iot-dashboard.vue rewrites the URL to its tab slug as soon as the page
  // loads, so a v2 user is never sitting at the bare /dashboard/iot above and
  // the exact match never fires -- they get served the Vue page instead (#28127).
  // These are listed explicitly rather than marking /dashboard/iot isDynamic:
  // the dynamic branch appends the matched remainder, which would build
  // /app/iot-devices/smart-devices (v2 selects the tab with ?tab=), and a
  // /dashboard/iot prefix would shadow /dashboard/iot/livion-devices/ below.
  '/dashboard/iot/smart-devices': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot-devices?tab=smart-devices',
  },
  '/dashboard/iot/key-automats': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot-devices?tab=key-automats',
  },
  '/dashboard/iot/door-code-settings': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot-devices?tab=door-code-settings',
  },
  '/dashboard/iot/livion-all-keys': {
    moduleKey: 'operations_iot',
    v2Route: '/app/livion-all-keys',
  },
  '/dashboard/iot/livion-devices/': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot/livion-devices/',
    isDynamic: true,
  },
  '/dashboard/auto-tasks': {
    moduleKey: 'operations_automated_tasks',
    v2Route: '/app/auto-task-rules',
  },
  '/dashboard/guest-mgmt': {
    moduleKey: 'guests_experience',
    v2Route: '/app/experience/pre-check-ins',
  },
  '/dashboard/auto-messages': {
    moduleKey: 'guests_automated_messages',
    v2Route: '/app/guests/automated-messages',
  },
  '/dashboard/cm': {
    moduleKey: 'marketing_channels',
    v2Route: '/app/channels',
  },
  '/dashboard/reviews-dashboard': {
    moduleKey: 'marketing_reviews',
    v2Route: '/app/reviews',
  },
  '/dashboard/pricing-panel': {
    moduleKey: 'marketing_pricing',
    v2Route: '/app/pricings',
  },
  '/dashboard/bank-feeds': {
    moduleKey: 'accounting_bank_feeds',
    v2Route: '/app/finance/bank-transactions',
  },
  '/dashboard/ai-config': {
    moduleKey: 'configuration_ai',
    v2Route: '/app/ai-config',
  },
  '/dashboard/management': {
    moduleKey: 'configuration_integrations',
    v2Route: '/app/integrations-v2',
  },
  '/dashboard/feeds': {
    moduleKey: 'feeds',
    v2Route: '/app/feeds',
    forceV2: true,
  },
  '/dashboard/guest-book': {
    moduleKey: 'guests_guest_book',
    v2Route: '/app/guest-book',
  },
  // The Vue ledger screens were deleted with the legacy ledger UI (#29967): the sidebar entry and
  // old bookmarks go to the React Ledger page for everyone.
  '/dashboard/ledger-wallet': {
    moduleKey: 'accounting_ledger',
    v2Route: '/app/dashboard/ledger-wallet',
    forceV2: true,
  },
  // the old route took an optional segment (ledger-wallet/:paramsTab?); it has no v2 counterpart
  '/dashboard/ledger-wallet/': {
    moduleKey: 'accounting_ledger',
    v2Route: '/app/dashboard/ledger-wallet',
    isPrefix: true,
    forceV2: true,
  },
  // one ledger account's transactions (management/ledger-accounts/:id) -> the Chart of Accounts,
  // where the account's row drills into the General Ledger
  '/dashboard/management/ledger-accounts/': {
    moduleKey: 'accounting_ledger',
    v2Route: '/app/dashboard/ledger-wallet',
    isPrefix: true,
    forceV2: true,
  },
  '/dashboard/invoices': {
    moduleKey: 'accounting_invoices',
    v2Route: '/app/dashboard/invoices',
  },
  '/dashboard/portfolio': {
    moduleKey: 'owners_portfolio',
    v2Route: '/app/dashboard/portfolio/overview',
  },
}

/**
 * Resolve a v1 route path to the v2 route it hands over to.
 *
 * Exact matches win; otherwise the first isDynamic or isPrefix entry whose key
 * prefixes the path matches. For isDynamic the remainder of the path (the :id)
 * is appended to its v2Route; isPrefix goes to the v2Route as it is (the v1
 * segment has no v2 counterpart). Callers still decide whether to actually redirect -- forceV2 goes
 * unconditionally, everything else only when the user's module is on v2.
 *
 * @param {string} routePath - the v1 path being navigated to
 * @returns {{moduleKey: string, v2Route: string, forceV2: boolean}|null}
 */
export const resolveV2Mapping = routePath => {
  let mapping = V2_ROUTE_MAPPINGS[routePath]
  let prefix = routePath

  if (!mapping) {
    for (const [key, config] of Object.entries(V2_ROUTE_MAPPINGS)) {
      if ((config.isDynamic || config.isPrefix) && routePath.startsWith(key)) {
        mapping = config
        prefix = key
        break
      }
    }
  }

  if (!mapping) return null

  // For dynamic routes, append the remaining path segment (e.g., the :id)
  const v2Route = mapping.isDynamic
    ? mapping.v2Route + routePath.slice(prefix.length)
    : mapping.v2Route

  return {
    moduleKey: mapping.moduleKey,
    v2Route,
    forceV2: Boolean(mapping.forceV2),
  }
}

/**
 * Full v2 URL a v1 path should hand over to, or null to stay on Vue.
 *
 * `user` is passed in rather than read from the store so this stays pure and
 * so both callers can use it: the router guard (which runs on every
 * navigation) and root-layout (which re-checks once the user has actually
 * loaded). On a cold page load the guard runs before getCurrentUser resolves,
 * so the guard alone leaves a bookmarked or refreshed v1 URL on Vue.
 *
 * @param {string} routePath - the v1 path being navigated to
 * @param {object|null} user - store.state.user, may be null during boot
 * @returns {string|null}
 */
export const resolveV2Redirect = (routePath, user) => {
  const mapping = resolveV2Mapping(routePath)
  if (!mapping) return null

  // A deprecated v1 screen goes to v2 for everyone, user loaded or not.
  if (mapping.forceV2) return `${getV2BaseUrl()}${mapping.v2Route}`

  const versionConfig = (user?.module_versions || {})[mapping.moduleKey]
  if (versionConfig?.module_version === 2) {
    return `${getV2BaseUrl()}${mapping.v2Route}`
  }

  return null
}
